

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function EnvLawsuitsSection({ caseId, sofaResponse }) {
  const [lawsuits, setLawsuits] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newLawsuit, setNewLawsuit] = useState({
    case_title: '',
    case_number: '',
    nature_of_case: '',
    court_name: '',
    court_street: '',
    court_city: '',
    court_state: '',
    court_zip: '',
    status: 'pending'
  });
  const [editLawsuit, setEditLawsuit] = useState(null);

  // Fetch existing environmental lawsuits
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-env-lawsuits/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch environmental lawsuits');
        setLawsuits(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Open Add Modal
  function openAddModal() {
    setNewLawsuit({
      case_title: '',
      case_number: '',
      nature_of_case: '',
      court_name: '',
      court_street: '',
      court_city: '',
      court_state: '',
      court_zip: '',
      status: 'pending'
    });
    setModalOpen(true);
  }

  // Open Edit Modal
  function openEditModal(item) {
    setEditLawsuit({ ...item });
    setEditModalOpen(true);
  }

  // Save new
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-env-lawsuits/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newLawsuit)
        }
      );
      if (!res.ok) throw new Error();
      const created = await res.json();
      setLawsuits(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to add environmental lawsuit', err);
    }
  }

  // Save edit
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-env-lawsuits/${editLawsuit.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editLawsuit)
        }
      );
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setLawsuits(prev => prev.map(l => l.id === updated.id ? updated : l));
      setEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update environmental lawsuit', err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-env-lawsuits/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error();
      setLawsuits(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error('Failed to delete environmental lawsuit', err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 26: Environmental Lawsuits
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List proceedings, lawsuits or settlements under an environmental law you have been party to.
        </p>
      {lawsuits.length === 0 ? (
        <p>No environmental lawsuits recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {lawsuits.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => { e.stopPropagation(); handleDelete(item.id); }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                       2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                       6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                       2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                       0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                       00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.case_title || '—'}</h3>
              <div className="mt-2 text-sm">
                <p><strong>Case #:</strong> {item.case_number || '—'}</p>
                <p><strong>Nature:</strong> {item.nature_of_case || '—'}</p>
                <p><strong>Court:</strong> {item.court_name || '—'}, {item.court_city || ''}</p>
                <p><strong>Status:</strong> {item.status}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Environmental Lawsuit
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Environmental Lawsuit</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Case Title</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newLawsuit.case_title}
                onChange={e => setNewLawsuit(prev => ({ ...prev, case_title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Case Number</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newLawsuit.case_number}
                  onChange={e => setNewLawsuit(prev => ({ ...prev, case_number: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nature of Case</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newLawsuit.nature_of_case}
                  onChange={e => setNewLawsuit(prev => ({ ...prev, nature_of_case: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Court Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newLawsuit.court_name}
                onChange={e => setNewLawsuit(prev => ({ ...prev, court_name: e.target.value }))}
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Court Street</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newLawsuit.court_street}
                onChange={e => setNewLawsuit(prev => ({ ...prev, court_street: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">Court City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newLawsuit.court_city}
                  onChange={e => setNewLawsuit(prev => ({ ...prev, court_city: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newLawsuit.court_state}
                  onChange={e => setNewLawsuit(prev => ({ ...prev, court_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newLawsuit.court_zip}
                  onChange={e => setNewLawsuit(prev => ({ ...prev, court_zip: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={newLawsuit.status}
                onChange={e => setNewLawsuit(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="pending">Pending</option>
                <option value="concluded">Concluded</option>
                <option value="appealed">Appealed</option>
              </select>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNew}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editLawsuit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Environmental Lawsuit</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Case Title</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editLawsuit.case_title}
                onChange={e => setEditLawsuit(prev => ({ ...prev, case_title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Case Number</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editLawsuit.case_number}
                  onChange={e => setEditLawsuit(prev => ({ ...prev, case_number: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nature of Case</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editLawsuit.nature_of_case}
                  onChange={e => setEditLawsuit(prev => ({ ...prev, nature_of_case: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Court Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editLawsuit.court_name}
                onChange={e => setEditLawsuit(prev => ({ ...prev, court_name: e.target.value }))}
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Court Street</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editLawsuit.court_street}
                onChange={e => setEditLawsuit(prev => ({ ...prev, court_street: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">Court City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editLawsuit.court_city}
                  onChange={e => setEditLawsuit(prev => ({ ...prev, court_city: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editLawsuit.court_state}
                  onChange={e => setEditLawsuit(prev => ({ ...prev, court_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editLawsuit.court_zip}
                  onChange={e => setEditLawsuit(prev => ({ ...prev, court_zip: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={editLawsuit.status}
                onChange={e => setEditLawsuit(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="pending">Pending</option>
                <option value="concluded">Concluded</option>
                <option value="appealed">Appealed</option>
              </select>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}